import { type CutStatus, cutStatusChangeMap } from '@consts/cut';

import type { RoleUtils } from '@/types/permissions';
import type { ParsedCut } from '@/types/cuts';
import type { CutType } from '@api/cut';

/**
 * Based on a provided role, get a list of all possible cut
 * statuses that can be transitioned to.
 */
export const getPossibleStatusChanges = (role: RoleUtils) => {
    // Loop through each cut type's CutStatusMap
    const set = Object.values(cutStatusChangeMap).reduce((set, curr) => {
        // For each PREVIOUS status, grab hold of the user's
        // possible NEW status changes.
        for (const statusName in curr) {
            const map = curr[statusName as CutStatus]!;
            if (!(role.type.name in map)) continue;

            set = new Set([...set, ...map[role.type.name]!]);
        }

        return set;
        // Use a set to avoid duplicate values
    }, new Set<CutStatus>());

    return [...set];
};

/**
 * Based on a provided role, cut, and new status, determine whether or not a cut's
 * status can be changed.
 */
export const canChangeStatus = ({
    role,
    cut,
    newStatus,
}: {
    role: RoleUtils;
    cut: ParsedCut;
    newStatus: CutStatus;
}) => {
    // Grab hold of the CutTypeMap based on the type of the
    // cut that we are editing, and then for the status  we
    // are changing.
    const target = cutStatusChangeMap[cut.cutType][cut.cutStatus];
    if (!target) return false;

    // Then, we know if the status can be changed based on it passing the following:
    // 1. The user's type is present, and it is an array
    // 2. That array includes the new status
    return Array.isArray(target[role.type.name]) && target[role.type.name]!.includes(newStatus);
};

type CutCounts = {
    [companyName: string]: {
        [locationName: string]: {
            [TypeOfCut in CutType]?: number;
        };
    };
};

/**
 * Get a map of all selected companies, where the values are maps
 * of locations, and location values contain the number of selected
 * cuts for each cut type.
 */
export const getCutCounts = (selectedCuts: Record<string, ParsedCut>) => {
    return Object.values(selectedCuts).reduce((acc, curr) => {
        acc[curr.company] ??= {};
        acc[curr.company][curr.location] ??= {};
        acc[curr.company][curr.location][curr.cutType] ??= 0;
        acc[curr.company][curr.location][curr.cutType]!++;
        return acc;
    }, {} as CutCounts);
};

export const validateCutStatusChanges = ({
    role,
    selectedCuts,
    status,
}: {
    role: RoleUtils;
    selectedCuts: Record<string, ParsedCut>;
    status: CutStatus | '';
}) => {
    const data: {
        readyToPatch: { cutIds: string[]; status: CutStatus };
        failedValidation: ParsedCut[];
        groupedFailedItems: [string, string[]][];
    } = {
        readyToPatch: { cutIds: [], status: status as CutStatus },
        failedValidation: [],
        groupedFailedItems: [],
    };

    if (!status) return data;

    Object.values(selectedCuts).reduce((acc, cut) => {
        // If we are attempting to change from, for example,
        // "Void" to "Void", this shouldn't fail validation, it should
        // just be ignored entirely.
        if (cut.cutStatus === status) return acc;

        // If we can't change the status of this cut, add it to the list
        // of failed changes so the user can be notified about it.
        if (canChangeStatus({ role, cut, newStatus: status })) {
            acc.readyToPatch.cutIds.push(cut.cutId);
            return acc;
        }

        // If all else fails, catch the failed cut by adding it to the
        // failed array.
        acc.failedValidation.push(cut);
        return acc;
    }, data);

    const groupedFailedItems = Object.entries(
        data.failedValidation.reduce((acc, curr) => {
            acc[curr.cutStatus] ??= [];
            acc[curr.cutStatus].push(curr.cutId);
            return acc;
        }, {} as Record<CutStatus, string[]>)
    );

    return { ...data, groupedFailedItems };
};
